import {gmAPI} from '../MapService';
import neRotateIcon from '@/assets/ne-rotate.png';
import nwRotateIcon from '@/assets/nw-rotate.png';
import swRotateIcon from '@/assets/sw-rotate.png';
import seRotateIcon from '@/assets/se-rotate.png';
import {radiansToPositiveDegrees} from '@/lib/ReusableFunctions/Formatters';

/** circle to be used for hidden rotators */
export const CIRCLE_SVG: google.maps.Symbol = {
  path: 'M 0, 0 m -150, 0 a 150,150 0 2,0 300,0 a 150,150 0 2,0 -300,0',
  fillOpacity: 0,
  strokeWeight: 2,
  scale: 0.1,
  strokeColor: '#FF00ff00',
};
/** circle to be used for the pivot marker */
export const PIVOT_SVG: google.maps.Symbol = {
  path: 'M 0, 0 m -75, 0 a 75,75 0 1,0 150,0 a 75,75 0 1,0 -150,0',
  fillOpacity: 1,
  strokeWeight: 2,
  scale: 0.1,
  fillColor: 'white',
};
/** square to be used as edge handles */
export const SQUARE_SVG: google.maps.Symbol = {
  path: 'M  -5, -5 L -5,5 L 5,5 L 5,-5 L -5,-5',
  fillOpacity: 1,
  fillColor: 'white',
  strokeWeight: 1,
  scale: 1,
};

/**
 * Given a starting point, horizontal distance, vertical distance, and angle, compute the new point.
 */
export const computeOffset = (
  start: google.maps.Point,
  horizontalDistance: number,
  verticalDistance: number,
  angle: number,
) => {
  const x = start.x + horizontalDistance * Math.cos(angle) - verticalDistance * Math.sin(angle);
  const y = start.y + horizontalDistance * Math.sin(angle) + verticalDistance * Math.cos(angle);
  return new gmAPI.Point(x, y);
};

/** Resize cursors for directions 0°, 45°, 90° and 135° clockwise from north; opposite directions share one. */
const SCALE_CURSORS = ['ns-resize', 'nesw-resize', 'ew-resize', 'nwse-resize'];

/**
 * Compute the cursor for an element's scaler.
 * @param elementRotation The rotation of the element in radians.
 * @param offset Optional, degrees to add to the element's rotation.
 */
export const getScaleCursor = (elementRotation: number, offset = 0) => {
  const rotationInDegrees = radiansToPositiveDegrees(elementRotation);
  const computedAngle = (rotationInDegrees + offset) % 360;
  // Each cursor covers the 45° sector centered on its direction (0° is north, clockwise), so a handle sitting on a
  // canonical angle such as a corner's 45° stays on its own cursor instead of flipping at the slightest rotation.
  const sector = Math.round(computedAngle / 45) % 4;
  return SCALE_CURSORS[sector];
};

/**
 * Compute the cursor for an element's rotator.
 * @param elementRotation The rotation of the element in radians.
 * @param offset Optional, degrees to add to the element's rotation.
 */
export const getRotatorCursor = (elementRotation: number, offset = 0) => {
  const rotationInDegrees = radiansToPositiveDegrees(elementRotation);
  const computedAngle = (rotationInDegrees + offset) % 360;

  if (computedAngle >= 0 && computedAngle < 91) {
    return 'url(' + neRotateIcon + ') 7.5 7.5, auto';
  }
  if (computedAngle > 90 && computedAngle <= 180) {
    return 'url(' + seRotateIcon + ') 7.5 7.5, auto';
  }
  if (computedAngle > 180 && computedAngle <= 270) {
    return 'url(' + swRotateIcon + ') 7.5 7.5, auto';
  }
  if (computedAngle > 270 && computedAngle <= 360) {
    return 'url(' + nwRotateIcon + ') 7.5 7.5, auto';
  }

  throw new Error('getRotatorCursor: Computed angle is out of bounds.');
};

/**
 * given the corners of a rectangle, return the edges of the rectangle
 */
export const getEdges = (corners: google.maps.LatLng[]) => {
  const edges = [];

  for (let i = 0; i < corners.length; i++) {
    edges[i] = new gmAPI.LatLng(
      (corners[i].lat() + corners[(i + 1) % 4].lat()) / 2,
      (corners[i].lng() + corners[(i + 1) % 4].lng()) / 2,
    );
  }

  return edges;
};

/**
 * Adds direction and sign properties to the Marker class
 */
export class Edge extends gmAPI.Marker {
  direction: string;
  sign: number;
  /**
   * Constructs a new Edge.
   */
  constructor(options: google.maps.MarkerOptions & {direction: string; sign: number}) {
    super(options);
    this.direction = options.direction;
    this.sign = options.sign;
  }
}
